import type { Tag } from '@/types';
import type { ComponentProps } from 'react';
import { Popover } from '@astryxdesign/core/Popover';
import { Token } from '@astryxdesign/core/Token';
import { useState } from 'react';

const TOKEN_COLORS: Record<string, ComponentProps<typeof Token>['color']> = {
  red: 'red',
  amber: 'yellow',
  violet: 'purple',
  blue: 'blue',
  teal: 'teal',
  slate: 'gray',
};

export function tagTokenColor(tag: Tag): ComponentProps<typeof Token>['color'] {
  return TOKEN_COLORS[tag.color] ?? 'gray';
}

interface TagPickerProps {
  tags: Tag[];
  appliedTagIds: string[];
  onAdd: (tagId: string) => void;
  onCreate: (label: string) => void;
}

export function TagPicker({ tags, appliedTagIds, onAdd, onCreate }: TagPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');

  const normalized = query.trim().toLowerCase();
  const activeTags = tags.filter((tag) => !tag.deleted);
  const suggestions = activeTags.filter(
    (tag) =>
      !appliedTagIds.includes(tag.id) &&
      (!normalized || tag.label.toLowerCase().includes(normalized))
  );
  const hasExactMatch =
    normalized !== '' && activeTags.some((tag) => tag.label.toLowerCase() === normalized);
  const canCreate = normalized !== '' && !hasExactMatch;

  const close = () => {
    setIsOpen(false);
    setQuery('');
  };

  const add = (tagId: string) => {
    onAdd(tagId);
    close();
  };

  const create = () => {
    onCreate(query.trim());
    close();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const match = activeTags.find((tag) => tag.label.toLowerCase() === normalized);
      if (match && !appliedTagIds.includes(match.id)) {
        add(match.id);
      } else if (canCreate) {
        create();
      }
    } else if (event.key === 'Escape') {
      close();
    }
  };

  return (
    <Popover
      label="Buscar o crear etiqueta"
      placement="below"
      alignment="start"
      width={288}
      isOpen={isOpen}
      onOpenChange={(open) => (open ? setIsOpen(true) : close())}
      content={
        <>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Buscar o crear etiqueta…"
            className="border-border bg-card text-primary focus:border-accent w-full rounded-lg border px-2.5 py-2 text-[13px] outline-none"
          />
          <div className="mt-1.5 flex max-h-48 flex-col gap-0.5 overflow-y-auto">
            {suggestions.map((tag) => (
              <button
                key={tag.id}
                type="button"
                onClick={() => add(tag.id)}
                className="hover:bg-surface flex cursor-pointer items-center rounded-lg px-1.5 py-1.5 text-left"
              >
                <Token label={tag.label} color={tagTokenColor(tag)} size="sm" />
              </button>
            ))}
            {canCreate && (
              <button
                type="button"
                onClick={create}
                className="text-accent hover:bg-surface cursor-pointer rounded-lg px-1.5 py-2 text-left text-[13px] font-bold"
              >
                + Crear “{query.trim()}”
              </button>
            )}
            {suggestions.length === 0 && !canCreate && (
              <div className="text-secondary px-1.5 py-2 text-xs">
                Escribí para crear una etiqueta nueva
              </div>
            )}
          </div>
          <p className="text-secondary mt-1.5 px-0.5 text-[11px]">
            Las etiquetas son compartidas por todo el equipo
          </p>
        </>
      }
    >
      <button
        type="button"
        className="border-border-strong text-secondary hover:border-accent hover:text-accent inline-flex h-5 cursor-pointer items-center gap-1 rounded border border-dashed bg-transparent px-2 text-[12px] font-medium transition-colors"
      >
        + etiqueta
      </button>
    </Popover>
  );
}
